import React, { useCallback, useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../../../axios-defaults";
import Aux from "../../../../hoc/Auxiliary";
import Spinner from "../../../UI/Spinner";
import SelectWrapper from "../../../Utils/Input/SelectWrapper";
import DateFilters from "../Tables/DateFilters";
import ApexChart from "./Chart/Chart";

import UserContext from '../../../../context/user-context';
import {userHaveSpecificPermission} from "../../../../components/Utils/UserHelper";

const Consumption = (props) => {
    const { user } = useContext(UserContext)
    const { subAccountCode, displayMode } = props;
    const isCardlock = displayMode === "K";
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    //Data
    const [cards, setCards] = useState([]); //Array of credit cards for filtering
    const [productTypes, setProductTypes] = useState([]); //Array of credit cards for filtering
    const [rawDatas, setRawDatas] = useState([]);
    const [data, setData] = useState([]);
    const [showLastYear, setShowLastYear] = useState(false);

    const currentYear = new Date().getFullYear();

    const firstYear = 2021;
    //Filters
    const [activeCard, setActiveCard] = useState(undefined); //active card number. Undefined for none
    const [activeYear, setActiveYear] = useState(currentYear);
    const [activeProductType, setActiveProductType] = useState(undefined); //active product type filter. Undefined for none
    const [activeDisplay, setActiveDisplay] = useState("L"); // "$" || "L"
    const [increase, setIncrease] = useState(0);
    const [averageLiters, setAverageLiters] = useState(0);
    const [averagePrices, setAveragePrices] = useState(0);
    const [seriesDatas, setSeriesDatas] = useState([]);
    const [labelLegend, setLabelLegend] = useState([]);

    const calculateIncrease = (thisYear, lastYear) => {
        if (lastYear === 0) return 100;
        const increase =
            Math.round(((thisYear - lastYear) / lastYear) * 10000) / 100; //two decimal places
        return increase;
    };

    const getCards = async () => {
        try {
            let cards = [];
            let pageNumber = 1;
            let totalPages = undefined;
            const pageSize = 50;
            //Handle pagination of cards if > 50
            do {
                const { data } = await axios.get(
                    `/subAccounts/cards?subAccountCode=${subAccountCode}&pageSize=${pageSize}&pageNumber=${pageNumber}`
                );
                if (data && data.data && data.data.length) {
                    //Do we have cards? add them to the array
                    cards = [...cards, ...data.data];
                }
                totalPages = data.totalPages;
                pageNumber++;
            } while (pageNumber <= totalPages);

            const mappedCards = cards
                .map((card) => {
                    const cardHolderMatchArray =
                        card.cardHolder.match(/[0-9]+/g); //Clean up cardholder to remove extra text
                    return {
                        cardHolder: cardHolderMatchArray !== null && cardHolderMatchArray.length
                            ? cardHolderMatchArray[0]
                            : card.cardHolder,
                        cardNumber: card.cardNumber,
                        last4: card.cardNumber.slice(
                            card.cardNumber.length - 4
                        ),
                        active: card.cardStatus.code === "000" ? true : false,
                    };
                })
                .filter((card) => card.active)
                .sort((a, b) => {
                    if (a.cardHolder > b.cardHolder) return 1;
                    if (a.cardHolder < b.cardHolder) return -1;
                    if (a.last4 > b.last4) return 1;
                    if (a.last4 < b.last4) return -1;
                    return 0;
                });

            setCards(mappedCards);
        } catch (e) {
            console.error(e);
        }
    };

    const getConsumption = useCallback(async () => {
        try {
            const { data } = await axios.get(
                `/subAccounts/monthlyConsumptions?subAccountCode=${subAccountCode}&year=${activeYear}${
                    activeCard ? `&cardNumber=${activeCard}` : ""
                }`
            );
            if (data && data.data && data.data.length) {
                setRawDatas(data.data);
            }
        } catch (e) {
            console.log(e);
            let parsedData = [];
            for (let x = 0; x < 12; x++) {
                parsedData.push({
                    month: x + 1,
                    currentAmountPurchases: 0,
                    currentAveragePrice: 0,
                    currentQuantityPurchases: 0,
                    priorYearAmountPurchases: 0,
                    priorYearAveragePrice: 0,
                    priorYearQuantityPurchases: 0,
                    productType: "DIS",
                });
            }
            setShowLastYear(false);
            setIncrease(0);
            setData(parsedData);
            setError(t("account.tabs.consumption.chart.no_data"));
        }
    }, [activeCard, activeYear, activeProductType, activeDisplay]);

	const roundToTwoDecimals = (num) => Number(num.toFixed(2));

    const getConsumptionData = useCallback(() => {
        if (rawDatas && rawDatas.length) {
			const monthDataMap = new Map();
            let productTypesData = [];

			// Initialize monthDataMap with empty values for each month
			for (let i = 0; i < 12; i++) {
				monthDataMap.set(i, {
					month: i + 1,
					currentAmountPurchases: 0,
					currentAveragePrice: 0,
					currentQuantityPurchases: 0,
					priorYearAmountPurchases: 0,
					priorYearAveragePrice: 0,
					priorYearQuantityPurchases: 0,
					productType: "DIS",
				});
			}

			// Process each data entry
			rawDatas.forEach((datum) => {
				const monthIndex = new Date(datum.transactionDate).getMonth();
				const monthData = monthDataMap.get(monthIndex);

				if (!activeProductType || datum.productType.code === activeProductType) {
					monthData.currentAmountPurchases += datum.currentAmountPurchases;
					monthData.currentQuantityPurchases += datum.currentQuantityPurchases;
					monthData.priorYearAmountPurchases += datum.priorYearAmountPurchases;
					monthData.priorYearQuantityPurchases += datum.priorYearQuantityPurchases;

					if (datum.currentQuantityPurchases > 0) {
						monthData.currentAveragePrice += datum.currentAveragePrice * datum.currentQuantityPurchases;
					}
					if (datum.priorYearQuantityPurchases > 0) {
						monthData.priorYearAveragePrice += datum.priorYearAveragePrice * datum.priorYearQuantityPurchases;
					}

					productTypesData.push(datum.productType);
					monthData.productType = datum.productType.code;
				}

				monthDataMap.set(monthIndex, monthData);
			});

			// Finalize average price calculation by dividing by total quantity
			const parsedData = Array.from(monthDataMap.values()).map((data) => {
				if (data.currentQuantityPurchases > 0) {
					data.currentAveragePrice = roundToTwoDecimals(data.currentAveragePrice / data.currentQuantityPurchases);
				}
				if (data.priorYearQuantityPurchases > 0) {
					data.priorYearAveragePrice = roundToTwoDecimals(data.priorYearAveragePrice / data.priorYearQuantityPurchases);
				}

				data.currentAmountPurchases = roundToTwoDecimals(data.currentAmountPurchases);
				data.currentQuantityPurchases = roundToTwoDecimals(data.currentQuantityPurchases);
				data.priorYearAmountPurchases = roundToTwoDecimals(data.priorYearAmountPurchases);
				data.priorYearQuantityPurchases = roundToTwoDecimals(data.priorYearQuantityPurchases);

				return data;
			});

            //fill product Types
            setProductTypes(productTypesData.filter((v,i,a)=>a.findIndex(t=>(t.code === v.code))===i));

            const thisYearPrices = parsedData.reduce(
                (a, b) =>
                    a + b.currentAmountPurchases,
                0
            );
            const thisYearLiters = parsedData.reduce(
                (a, b) =>
                    a + b.currentQuantityPurchases,
                0
            );
            const lastYearPrices = parsedData.reduce(
                (a, b) =>
                    a + b.priorYearAmountPurchases,
                0
            );
            const lastYearLiters = parsedData.reduce(
                (a, b) =>
                    a + b.priorYearQuantityPurchases,
                0
            );
            const increase = calculateIncrease(thisYearLiters, lastYearLiters);
            setIncrease(increase);
			const averageLiters = roundToTwoDecimals(thisYearLiters / 12);
			const averagePrices = roundToTwoDecimals(thisYearPrices / 12);
            setAverageLiters(averageLiters);
            setAveragePrices(averagePrices);

            const tmpShowLastYear = lastYearLiters > 0;
            setShowLastYear(tmpShowLastYear);
            setData(parsedData);

            setError(undefined);
        }

    }, [rawDatas, activeProductType]);

    useEffect(() => {
        //Get data
        getCards();
    }, []);

    useEffect(() => {
        getConsumption();
    }, [activeCard, activeYear]);

    useEffect(() => {
        getConsumptionData();
    }, [rawDatas, activeProductType]);

    useEffect(() => {
        let tmpLabelLegend = [
			t("account.tabs.consumption.chart.this_year")
		];

		if (isCardlock && userHaveSpecificPermission(user, 'financial_data')) {
			tmpLabelLegend.push(t("account.tabs.consumption.chart.average_price_ty"));
		}

		let tmpSeriesDatas = [];

		if (isCardlock) {
			tmpSeriesDatas.push({
				name: t(
					"account.tabs.consumption.chart.this_year"
				),
				type: "column",
				data: data.map((datum) =>
					activeDisplay === "L"
						? datum.currentQuantityPurchases
						: datum.currentAmountPurchases
				),
			});

			if (userHaveSpecificPermission(user, 'financial_data')) {
				tmpSeriesDatas.push({
					name: t(
						"account.tabs.consumption.chart.average_price_ty"
					),
					type: "line",
					data: data.map(
						(datum) =>
							datum.currentAveragePrice
					),
					show: false
				});
			}
		} else {
			tmpSeriesDatas.push({
					name: t(
						"account.tabs.consumption.chart.this_year"
					),
					type: "column",
					data: data.map(
						(datum) =>
							datum.currentAmountPurchases
					),
				},
				{
					name: 'tmp',
					type: "line",
					data: data.map(
						(datum) =>
							0
					),
				})
		}

        if (showLastYear) {
            if (isCardlock) {
                tmpLabelLegend.push(t("account.tabs.consumption.chart.last_year"));
                tmpSeriesDatas.push(
					{
						name: t(
							"account.tabs.consumption.chart.last_year"
						),
						type: "column",
						data: data.map((datum) =>
							activeDisplay === "L"
								? datum.priorYearQuantityPurchases
								: datum.priorYearAmountPurchases
						),
					})

				if (userHaveSpecificPermission(user, 'financial_data')){
					tmpLabelLegend.push(t("account.tabs.consumption.chart.average_price_ly"));
					tmpSeriesDatas.push({
						name: t(
							"account.tabs.consumption.chart.average_price_ly"
						),
						type: "line",
						data: data.map(
							(datum) =>
								datum.priorYearAveragePrice
						),
						show: false
					})
				}
            } else {
                tmpLabelLegend.push(t("account.tabs.consumption.chart.last_year"))
				tmpSeriesDatas.push(
					{
						name: t(
							"account.tabs.consumption.chart.last_year"
						),
						type: "column",
						data: data.map(
							(datum) =>
								datum.priorYearAmountPurchases
						),
					}
				)
            }
        }
        setLabelLegend(tmpLabelLegend);
        setSeriesDatas(tmpSeriesDatas);

    }, [data, showLastYear, t, activeDisplay]);

    useEffect(() => {
        //Disable spinner after data load
        cards.length && seriesDatas.length && setLoading(false);
    }, [cards, seriesDatas]);


    return (
        <Aux>
            <div className="flex flex-column flex-sm-row flex-wrap justify-content-between align-items-center">
                <h2 className="headline headline--section headline--uppercase">
                    {t("account.tabs.consumption.title")}
                </h2>
                <div className="flex top-actions top-actions--early mb-4">
                    <div className="datatable__filters flex mr-0 mr-xs-2">
                        {isCardlock ? (
                            <>
                                <div className="mb-3 mb-sm-0 mr-2">
                                    <SelectWrapper>
                                        <select id="consumption-type-filters"
                                            onChange={(e) =>
                                                setActiveDisplay(
                                                    e.target.value || "$"
                                                )
                                            }
                                            value={activeDisplay}
                                        >
                                            <option value="L">
                                                {t(
                                                    "account.tabs.consumption.chart.volume"
                                                )}
                                            </option>
                                            {(() => {
                                                if (userHaveSpecificPermission(user, 'financial_data')) {
                                                    return (
                                                        <option value="$">
                                                            {t(
                                                                "account.tabs.consumption.chart.amount"
                                                            )}
                                                        </option>
                                                    )
                                                }
                                            })()}

                                        </select>
                                    </SelectWrapper>
                                </div>
                                <div className="mb-3 mb-sm-0 mr-2">
                                    <SelectWrapper>
                                        <select id="product-filters"
                                            onChange={(e) => {
                                                setActiveProductType(
                                                    e.target.value || undefined
                                                );
                                            }}
                                            value={activeProductType}
                                        >
                                            <option value="">
                                                {t(
                                                    "account.tabs.consumption.chart.product_type_filter"
                                                )}
                                            </option>
                                            {productTypes.map((type, i) => (
                                                <option
                                                    key={i}
                                                    value={type.code}
                                                >
                                                    {i18n.language === "fr"
                                                        ? type.descriptionFr
                                                        : type.descriptionEn
                                                    }
                                                </option>
                                            ))}
                                        </select>
                                    </SelectWrapper>
                                </div>
                            </>
                        ) : null}
                        <SelectWrapper>
                            <select id="cardholder-filters"
                                onChange={(e) => {
                                    setLoading(true);
                                    setActiveCard(e.target.value || undefined);
                                }}
                                value={activeCard}
                            >
                                <option key="Card holder" value="">
                                    {t(
                                        "account.tabs.consumption.chart.card_filter"
                                    )}
                                </option>
                                {cards.map((card, i) => {
                                    return (
                                        <option value={card.cardNumber} key={i}>
                                            {card.cardHolder} ({card.last4})
                                        </option>
                                    );
                                })}
                            </select>
                        </SelectWrapper>
                    </div>
                </div>
            </div>
            <div className="flex justify-content-between char-wrapper-fix" >
                <div className="w-100 h-100 position-relative flex-grow-0 flex-shrink-1">
                    {loading ? (
                        <div
                            className="position-absolute top-0 left-0 flex flex-col h-100 w-100 justify-content-center align-items-center"
                            style={{
                                backgroundColor: "rgba(255,255,255,0.6)",
                                zIndex: 1,
                            }}
                        >
                            <Spinner />
                        </div>
                    ) : null}
                    <ApexChart
                        activeDisplay={activeDisplay}
                        error={error}
                        series={seriesDatas}
                        options={{
                            chart: {
                                height: 350,
                                width: "100%",
                                type: "line",
                                stacked: false,
                                zoom: {
                                    enabled: false,
                                },
                                toolbar: {
                                    show: false,
                                },
                            },
                            dataLabels: {
                                enabled: false,
                            },
                            stroke: {
                                dashArray: [0, 0, 2, 2],
                                width: [1, 1, 2, 2],
                            },
							colors: userHaveSpecificPermission(user, 'financial_data') ? [
									"#F2994A",
									"#EE1B21",
									"#ececec",
									"#74E767",
								] :
								[
									"#F2994A",
									"#ececec",
								],
                            xaxis: {
                                categories: t(
                                    "account.tabs.consumption.chart.months"
                                ).split(","),
                            },
                            yaxis: isCardlock
                                ? [
                                      {
                                          seriesName: t(
                                              "account.tabs.consumption.chart.this_year"
                                          ),
                                          axisTicks: {
                                              show: true,
                                          },
                                          axisBorder: {
                                              show: true,
                                              color: "#DCDCDC",
                                          },
                                          labels: {
                                              style: {
                                                  colors: "#333333",
                                              },
                                              // formatter: function (val) {
                                              //     return val.toFixed(2) + ' ' + activeDisplay;
                                              // }
                                          },
                                          title: {
                                              text:
                                                  activeDisplay === "L"
                                                      ? t(
                                                            "account.tabs.consumption.chart.volume"
                                                        )
                                                      : t(
                                                            "account.tabs.consumption.chart.amount"
                                                        ),
                                              style: {
                                                  color: "#333333",
                                              },
                                          },
                                          tooltip: {
                                              enabled: false,
                                          },
                                      },
                                      {
                                          seriesName: t(
                                              "account.tabs.consumption.chart.average_price_ty"
                                          ),
										  show: userHaveSpecificPermission(user, 'financial_data'),
                                          opposite: true,
                                          axisTicks: {
                                              show: true,
                                          },
                                          axisBorder: {
                                              show: true,
                                              color: "#DCDCDC",
                                          },
                                          labels: {
                                              style: {
                                                  colors: "#333333",
                                              },
                                              // formatter: function (val) {
                                              //     return val.toFixed(2) + " $";
                                              // },
                                          },
                                          title: {
                                              text: t(
                                                  "account.tabs.consumption.chart.average_price"
                                              ),
                                              style: {
                                                  color: "#333333",
                                              },
                                          },
                                      },
                                    {
                                        seriesName: t("account.tabs.consumption.chart.this_year"),
                                        show:false
                                    },
                                    {
                                        seriesName: t("account.tabs.consumption.chart.average_price_ty"),
                                        opposite:true,
                                        show:false
                                    }
                                  ]
                                : [
                                      {
                                          seriesName: t(
                                              "account.tabs.consumption.chart.average_price"
                                          ),
										  show: userHaveSpecificPermission(user, 'financial_data'),
                                          axisTicks: {
                                              show: true,
                                          },
                                          axisBorder: {
                                              show: true,
                                              color: "#DCDCDC",
                                          },
                                          labels: {
                                              style: {
                                                  colors: "#333333",
                                              },
                                              formatter: function (val) {
                                                  return val + " $";
                                              }
                                          },
                                          title: {
                                              text: t(
                                                  "account.tabs.consumption.chart.amount"
                                              ),
                                              style: {
                                                  color: "#333333",
                                              },
                                          },
                                          tooltip: {
                                              enabled: false,
                                          },
                                      },
                                    {
                                        seriesName: 'tmp',
                                        axisTicks: {
                                            show: false,
                                        },
                                        axisBorder: {
                                            show: false,
                                        },
                                        labels: {
                                            show: false,
                                        },
                                        tooltip: {
                                            enabled: false,
                                        },
                                    }

                                  ],
                            tooltip: {
                                enabledOnSeries: isCardlock?undefined:[0],
                                fixed: {
                                    enabled: false,
                                    position: "topLeft", // topRight, topLeft, bottomRight, bottomLeft
                                    offsetY: 30,
                                    offsetX: 60,
                                },
                            },
                            legend: {
                                horizontalAlign: "left",
                                customLegendItems: labelLegend,
                                offsetX: 40,
                            },
                        }}
                    />
                    <div className="year-slider__wrapper">
                        <i
                            className={`fas fa-caret-left fa-2x cursor-pointer ${
                                activeYear - 1 > firstYear
                                    ? "cursor-pointer"
                                    : "cursor-na"
                            }`}
                            onClick={() => {
                                if (activeYear - 1 > firstYear) {
                                    setError(undefined);
                                    setLoading(true);
                                    setActiveYear((activeYear) => activeYear - 1);
                                }
                            }}
                        />
                        <div
                            className="year-slider"
                            ref={(yearsSliderRef) => yearsSliderRef}
                        >
                            <div
                                className={`year-slider__inner cursor-pointer ${
                                    activeYear - 1 > firstYear
                                        ? "cursor-pointer"
                                        : "cursor-na"
                                }`}
                                onClick={() => {
                                    if (activeYear - 1 > firstYear) {setError(undefined);
                                        setLoading(true);
                                        setActiveYear(
                                            (activeYear) => activeYear - 1
                                        );
                                    }
                                }}
                            >
                                <span className="year-slider__year">
                                    {activeYear - 1}
                                </span>
                            </div>
                            <div className="year-slider__inner">
                                <span className="year-slider__year">
                                    {activeYear}
                                </span>
                            </div>
                            <div
                                className={`year-slider__inner ${
                                    activeYear + 1 <= currentYear
                                        ? "cursor-pointer"
                                        : "cursor-na"
                                }`}
                                onClick={() => {
                                    if (activeYear + 1 <= currentYear) {
                                        setError(undefined);
                                        setLoading(true);
                                        setActiveYear(
                                            (activeYear) => activeYear + 1
                                        );
                                    }
                                }}
                            >
                                {activeYear + 1 <= currentYear ? (
                                    <span className="year-slider__year">
                                        {activeYear + 1}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        <i
                            className={`fas fa-caret-right fa-2x ${
                                activeYear + 1 <= currentYear
                                    ? "cursor-pointer"
                                    : "cursor-na"
                            }`}
                            onClick={() => {
                                if (activeYear + 1 <= currentYear) {
                                    setError(undefined);
                                    setLoading(true);
                                    setActiveYear(
                                        (activeYear) => activeYear + 1
                                    );
                                }
                            }}
                        />
                    </div>
                </div>
                <div className="d-none flex-column flex-grow-1 flex-shrink-0 justify-content-center mb-5 p-5">
                    <div className="flex flex-column">
                        <span>
                        {/*    {t("account.tabs.consumption.average.title_" + activeDisplay)}*/}
                        </span>
                        <div className="flex flex-row mt-1">
                            <span className="h3">
                        {/*        {activeDisplay === "L"*/}
                        {/*            ? t(*/}
                        {/*                  "account.tabs.consumption.average.litres",*/}
                        {/*                  { value: averageLiters.toString() }*/}
                        {/*              )*/}
                        {/*            : t(*/}
                        {/*                  "account.tabs.consumption.average.dollars",*/}
                        {/*                  { value: averagePrices.toString() }*/}
                        {/*              )}*/}
                            </span>
                        {/*    &nbsp;*/}
                            <span>
                        {/*        {t(*/}
                        {/*            "account.tabs.consumption.average.per_month"*/}
                        {/*        )}*/}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-column">
                        <span>
                        {/*    {t("account.tabs.consumption.compare.title")}*/}
                        </span>
                        <div className="flex flex-row mt-1">
                            <span className="h4">
                        {/*        {increase > 0 ? "+" : "-"} {Math.abs(increase)}%*/}
                            </span>
                        {/*    &nbsp;*/}
                        {/*    {increase > 0 ? (*/}
                        {/*        <i className="fas fa-arrow-up" />*/}
                        {/*    ) : (*/}
                        {/*        <i className="fas fa-arrow-down" />*/}
                        {/*    )}*/}
                        </div>
                    </div>
                </div>
            </div>
            {/*<div className="flex flex-row mt-5">*/}
            {/*    <div*/}
            {/*        className="flex flex-row justify-content-center align-items-center p-4"*/}
            {/*        style={{ backgroundColor: "rgba(238, 27, 33, 1)" }}*/}
            {/*    >*/}
            {/*        <i*/}
            {/*            className="fas fa-exclamation-triangle"*/}
            {/*            style={{ color: "#fff" }}*/}
            {/*        />*/}
            {/*    </div>*/}
            {/*    <div*/}
            {/*        className="flex flex-column justify-content-center align-items-start p-4"*/}
            {/*        style={{ backgroundColor: "rgba(238, 27, 33, 0.1)" }}*/}
            {/*    >*/}
            {/*        <p*/}
            {/*            style={{*/}
            {/*                color: "rgba(238, 27, 33, 1)",*/}
            {/*                fontWeight: 700,*/}
            {/*            }}*/}
            {/*        >*/}
            {/*            {t("account.tabs.consumption.high_consumption")}*/}
            {/*        </p>*/}
            {/*        <p>*/}
            {/*            Sit tempus, auctor nunc eu nam egestas orci egestas.*/}
            {/*            Vivamus egestas sit pharetra, sem duis faucibus aliquet*/}
            {/*            purus. Massa aliquet arcu, sodales arcu a nibh ac.*/}
            {/*            Aliquam ut posuere morbi mauris, faucibus elementum*/}
            {/*            consectetur auctor. Et, risus posuere aliquam pretium*/}
            {/*            eget. Duis sed pulvinar tortor fermentum dictum ante*/}
            {/*            neque, rhoncus aliquam. Eu massa condimentum vestibulum*/}
            {/*            pharetra pellentesque lectus euismod. At enim malesuada*/}
            {/*            aliquet tristique auctor massa eleifend tortor.*/}
            {/*        </p>*/}
            {/*    </div>*/}
            {/*</div>*/}
        </Aux>
    );
};

export default Consumption;
